import * as React from "react";
import { AlertTriangle, FileText, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Dropzone } from "@/components/ui/dropzone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ApiError,
  deleteRequestDocument,
  requestDocumentFileUrl,
  uploadRequestDocument
} from "../../api";
import { RequestDocument } from "../../types";

// Matches the bill uploader's limit (ExpensesStep.tsx's MAX_BILL_FILE_BYTES)
// and the backend's Flask MAX_CONTENT_LENGTH - rejected here, client-side, so
// an oversized file never round-trips just to be bounced.
const MAX_DOCUMENT_FILE_BYTES = 50 * 1024 * 1024;
const MAX_DOCUMENT_NOTE_LENGTH = 500;

function formatFileSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

let nextLocalId = 0;

type PendingState = "uploading" | "failed";

interface PendingDocument {
  localId: string;
  file: File;
  note: string;
  state: PendingState;
}

/** Supporting documents - plain reference attachments (visa, boarding pass,
 *  itinerary, hotel confirmation, etc.) with an optional note. Deliberately
 *  far simpler than ExpensesStep: no AI analysis, no duplicate check, no
 *  policy limits, no amount/category. Each dropped file uploads immediately
 *  (there's nothing to review/edit first, unlike a bill's extracted fields);
 *  the optional note is attached from the pending card before it commits. */
export function DocumentsStep({
  travelRequestId,
  documents,
  onChanged,
  onBusyChange
}: {
  travelRequestId: string;
  documents: RequestDocument[];
  onChanged: (documents: RequestDocument[]) => void;
  /** Fired whenever an upload is still in flight - the wizard disables Next
   *  while it is, same as the Bills step does for an in-flight analysis, so a
   *  click-through can't leave the step mid-upload. */
  onBusyChange?: (busy: boolean) => void;
}) {
  const [pending, setPending] = React.useState<PendingDocument[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<string | null>(null);

  const busy = pending.some((p) => p.state === "uploading");
  React.useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  // Report "not busy" on unmount so a stale `true` can't outlive this step
  // (e.g. the user navigates away mid-upload) and leave Next disabled
  // elsewhere forever - same reasoning as ExpensesStep's own unmount effect.
  React.useEffect(() => {
    return () => onBusyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchPending = (localId: string, patch: Partial<PendingDocument>) => {
    setPending((prev) =>
      prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p))
    );
  };

  const removePending = (localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId));
  };

  const upload = async (doc: PendingDocument, note: string) => {
    patchPending(doc.localId, { state: "uploading", note });
    try {
      const updated = await uploadRequestDocument(
        travelRequestId,
        doc.file,
        note || undefined
      );
      onChanged(updated.documents || []);
      removePending(doc.localId);
    } catch (err) {
      patchPending(doc.localId, { state: "failed" });
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not upload this document. Please try again.";
      setError(message);
      toast.error(message);
    }
  };

  const onFilesSelected = (files: File[]) => {
    setError(null);
    for (const file of files) {
      nextLocalId += 1;
      const doc: PendingDocument = {
        localId: `doc-${nextLocalId}`,
        file,
        note: "",
        state: "uploading"
      };
      setPending((prev) => [...prev, doc]);
      // Uploaded straight away - there are no fields to review first. The
      // note can still be added via "Retry"/edit on a failed card, but the
      // common path is drop-and-done.
      void upload(doc, "");
    }
  };

  const onFilesRejected = (files: File[]) => {
    const oversized = files.filter((f) => f.size > MAX_DOCUMENT_FILE_BYTES);
    const wrongType = files.filter((f) => f.size <= MAX_DOCUMENT_FILE_BYTES);
    const parts: string[] = [];
    if (oversized.length === 1) {
      parts.push(
        `"${oversized[0].name}" is ${formatFileSize(oversized[0].size)} - files over 50MB can't be uploaded.`
      );
    } else if (oversized.length > 1) {
      parts.push(`${oversized.length} files are over the 50MB limit.`);
    }
    if (wrongType.length === 1) {
      parts.push(`"${wrongType[0].name}" isn't an image or PDF.`);
    } else if (wrongType.length > 1) {
      parts.push(`${wrongType.length} files aren't images or PDFs.`);
    }
    const message = `${parts.join(" ")} Not added.`;
    setError(message);
    toast.error(message);
  };

  const remove = async (documentId: string) => {
    try {
      const updated = await deleteRequestDocument(travelRequestId, documentId);
      onChanged(updated.documents || []);
      setRemoveTarget(null);
      toast.success("Document removed");
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Could not remove this document - check your connection and try again."
      );
    }
  };

  return (
    <div className="min-w-0 space-y-4">
      <div>
        <Label>Upload documents</Label>
        <p className="text-xs text-muted-foreground">
          Attach any supporting files - visa, boarding pass, itinerary, hotel
          confirmation, and so on. These are for reference only and don't
          affect your claim.
        </p>
      </div>

      <Dropzone
        accept="image/*,.pdf"
        multiple
        onFilesSelected={onFilesSelected}
        maxSizeBytes={MAX_DOCUMENT_FILE_BYTES}
        onFilesRejected={onFilesRejected}
        allowCameraCapture
      />

      {pending.length > 0 && (
        <div className="space-y-3">
          {pending.map((doc) => (
            <div
              key={doc.localId}
              className="min-w-0 space-y-3 rounded-md border border-primary bg-primary/5 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
                  {doc.state === "uploading" ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                  )}
                  <span className="truncate">{doc.file.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => removePending(doc.localId)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {doc.state === "uploading" ? (
                <p className="text-xs text-muted-foreground">Uploading…</p>
              ) : (
                <div className="space-y-2">
                  <div className="space-y-1.5">
                    <Label>Note (optional)</Label>
                    <Input
                      value={doc.note}
                      maxLength={MAX_DOCUMENT_NOTE_LENGTH}
                      onChange={(e) =>
                        patchPending(doc.localId, { note: e.target.value })
                      }
                      placeholder="e.g. Return boarding pass"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => void upload(doc, doc.note)}
                  >
                    Retry upload
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {documents.length > 0 && (
        <p className="text-xs font-medium text-muted-foreground">
          Uploaded documents ({documents.length})
        </p>
      )}

      <div className="space-y-2">
        {documents.map((document) => (
          <div
            key={document.id}
            className="flex min-w-0 items-center gap-3 rounded-md border p-3 text-sm"
          >
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <a
                href={requestDocumentFileUrl(travelRequestId, document.id)}
                target="_blank"
                rel="noreferrer"
                title={document.filename || undefined}
                className="block truncate text-primary underline"
              >
                {document.filename || "Document"}
              </a>
              {document.note && (
                <p className="truncate text-xs text-muted-foreground">
                  {document.note}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => setRemoveTarget(document.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {documents.length === 0 && pending.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No documents uploaded yet.
          </p>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove this document?"
        description="This document will be permanently removed from the request."
        confirmLabel="Remove"
        destructive
        onConfirm={() => removeTarget && remove(removeTarget)}
      />
    </div>
  );
}
